/**
 * Collaborative Revision Review Modal
 * Allows document collaborators to review, comment on, and vote for different AI-generated revisions
 */

import React, { useState, useEffect } from 'react';
import { X, MessageSquare, ThumbsUp, ThumbsDown, Users, Eye, Clock, CheckCircle, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { api } from '../utils/api';

export function CollaborativeRevisionReview({ 
  revisions,
  documentId, 
  onClose,
  onImplement,
  currentUser,
  isOwner = false
}) {
  const [selectedRevision, setSelectedRevision] = useState(0);
  const [comments, setComments] = useState({});
  const [votes, setVotes] = useState({});
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [reviewStatus, setReviewStatus] = useState('open'); // open, closed, implemented

  useEffect(() => {
    loadCollaborativeData();
  }, [documentId]);

  const loadCollaborativeData = async () => {
    setLoading(true);
    try {
      // Load collaborators
      const collabResponse = await api.request(`/collaborators/document/${documentId}`);
      setCollaborators(collabResponse.collaborators || []);
      
      // Load existing comments and votes for these revisions
      const reviewResponse = await api.request(`/revision-review/${documentId}/current`);
      if (reviewResponse) {
        setComments(reviewResponse.comments || {});
        setVotes(reviewResponse.votes || {});
        setReviewStatus(reviewResponse.status || 'open');
      }
    } catch (err) {
      console.error('Failed to load collaborative data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setSubmitting(true);
    try {
      const response = await api.request(`/revision-review/${documentId}/comment`, {
        method: 'POST',
        body: JSON.stringify({
          revision_number: selectedRevision + 1,
          comment: newComment.trim(),
          user_id: currentUser.id,
          user_name: currentUser.full_name || currentUser.email
        })
      });

      // Update local state
      const revisionKey = `revision_${selectedRevision + 1}`;
      setComments(prev => ({
        ...prev,
        [revisionKey]: [...(prev[revisionKey] || []), response.comment]
      }));
      
      setNewComment('');
    } catch (err) {
      alert('Failed to add comment: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (revisionNumber, voteType) => {
    setSubmitting(true);
    try {
      const response = await api.request(`/revision-review/${documentId}/vote`, {
        method: 'POST',
        body: JSON.stringify({
          revision_number: revisionNumber,
          vote: voteType, // 'prefer', 'approve', 'reject'
          user_id: currentUser.id,
          user_name: currentUser.full_name || currentUser.email
        })
      });

      // Update local state
      const revisionKey = `revision_${revisionNumber}`;
      setVotes(prev => ({
        ...prev,
        [revisionKey]: {
          ...prev[revisionKey],
          [currentUser.id]: { vote: voteType, user_name: currentUser.full_name || currentUser.email }
        }
      }));
    } catch (err) {
      alert('Failed to record vote: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getVoteSummary = (revisionNumber) => {
    const revisionKey = `revision_${revisionNumber}`;
    const revisionVotes = votes[revisionKey] || {};
    
    const voteCounts = {
      prefer: 0,
      approve: 0,
      reject: 0
    };

    Object.values(revisionVotes).forEach(vote => {
      if (vote.vote) voteCounts[vote.vote]++;
    });

    return voteCounts;
  };

  const getMostPreferredRevision = () => {
    let maxPrefers = 0;
    let preferredRevision = 1;
    
    revisions.forEach((_, index) => {
      const voteSummary = getVoteSummary(index + 1);
      if (voteSummary.prefer > maxPrefers) {
        maxPrefers = voteSummary.prefer;
        preferredRevision = index + 1;
      }
    });
    
    return { revision: preferredRevision, count: maxPrefers };
  };

  const handleImplementRevision = async (revisionIndex) => {
    if (!window.confirm(`Implement Revision ${revisionIndex + 1} as the final version? This will replace the current document content.`)) {
      return;
    }

    try {
      await onImplement(revisions[revisionIndex].text);
      
      // Mark review as implemented
      await api.request(`/revision-review/${documentId}/implement`, {
        method: 'POST',
        body: JSON.stringify({
          implemented_revision: revisionIndex + 1,
          implemented_by: currentUser.id
        })
      });

      setReviewStatus('implemented');
    } catch (err) {
      alert('Failed to implement revision: ' + err.message);
    }
  };

  const activeCollaborators = collaborators.filter(c => c.status === 'active').length;
  const totalVotes = Object.keys(votes).reduce((total, key) => total + Object.keys(votes[key]).length, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-2xl">Collaborative Revision Review</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  {revisions.length} versions • {activeCollaborators} collaborators • {totalVotes} votes cast
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {reviewStatus === 'implemented' && (
                <div className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  Implemented
                </div>
              )}
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Consensus Summary */}
          {totalVotes > 0 && (
            <Alert className="mt-4 bg-blue-50 border-blue-200">
              <Star className="h-4 w-4 text-blue-600" />
              <AlertDescription>
                <strong>Team Consensus:</strong> Revision {getMostPreferredRevision().revision} is most preferred 
                ({getMostPreferredRevision().count} votes)
                {isOwner && reviewStatus === 'open' && (
                  <Button 
                    size="sm" 
                    className="ml-3"
                    onClick={() => handleImplementRevision(getMostPreferredRevision().revision - 1)}
                  >
                    Implement This Version
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Revision Tabs */}
          <div className="w-64 border-r flex flex-col">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-semibold text-sm text-gray-700">REVISIONS</h3>
            </div>
            <div className="flex-1 overflow-y-auto">
              {revisions.map((revision, index) => {
                const voteSummary = getVoteSummary(index + 1);
                const hasMyVote = votes[`revision_${index + 1}`]?.[currentUser.id];
                
                return (
                  <button
                    key={index}
                    onClick={() => setSelectedRevision(index)}
                    className={`w-full p-4 text-left border-b transition-colors hover:bg-gray-50 ${
                      selectedRevision === index
                        ? 'bg-blue-50 border-l-4 border-l-blue-600'
                        : 'border-l-4 border-l-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Version {index + 1}</span>
                      {hasMyVote && (
                        <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          You: {hasMyVote.vote}
                        </div>
                      )}
                    </div>
                    
                    <div className="text-xs text-gray-600 mb-2">
                      {revision.words} words
                    </div>
                    
                    {/* Vote summary */}
                    <div className="space-y-1">
                      {voteSummary.prefer > 0 && (
                        <div className="text-xs flex items-center gap-1 text-green-700">
                          <ThumbsUp className="w-3 h-3" />
                          {voteSummary.prefer} prefer
                        </div>
                      )}
                      {voteSummary.approve > 0 && (
                        <div className="text-xs flex items-center gap-1 text-blue-700">
                          <CheckCircle className="w-3 h-3" />
                          {voteSummary.approve} approve
                        </div>
                      )}
                      {voteSummary.reject > 0 && (
                        <div className="text-xs flex items-center gap-1 text-red-700">
                          <ThumbsDown className="w-3 h-3" />
                          {voteSummary.reject} reject
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 flex flex-col">
            {/* Revision Content */}
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="prose max-w-none">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Version {selectedRevision + 1}</h3>
                  
                  {/* Quick Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant={votes[`revision_${selectedRevision + 1}`]?.[currentUser.id]?.vote === 'prefer' ? 'default' : 'outline'}
                      onClick={() => handleVote(selectedRevision + 1, 'prefer')}
                      disabled={submitting || reviewStatus !== 'open'}
                    >
                      <Star className="w-4 h-4 mr-1" />
                      Prefer
                    </Button>
                    <Button
                      size="sm"
                      variant={votes[`revision_${selectedRevision + 1}`]?.[currentUser.id]?.vote === 'approve' ? 'default' : 'outline'}
                      onClick={() => handleVote(selectedRevision + 1, 'approve')}
                      disabled={submitting || reviewStatus !== 'open'}
                    >
                      <ThumbsUp className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant={votes[`revision_${selectedRevision + 1}`]?.[currentUser.id]?.vote === 'reject' ? 'destructive' : 'outline'}
                      onClick={() => handleVote(selectedRevision + 1, 'reject')}
                      disabled={submitting || reviewStatus !== 'open'}
                    >
                      <ThumbsDown className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-l-blue-500">
                  <p className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                    {revisions[selectedRevision].text}
                  </p>
                </div>
              </div>
            </div>

            {/* Comments Section */}
            <div className="border-t bg-gray-50 p-6 max-h-80 overflow-y-auto">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="w-5 h-5 text-gray-600" />
                <h4 className="font-semibold">Comments for Version {selectedRevision + 1}</h4>
              </div>

              {/* Existing Comments */}
              <div className="space-y-3 mb-4">
                {(comments[`revision_${selectedRevision + 1}`] || []).map((comment, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{comment.user_name}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{comment.comment}</p>
                  </div>
                ))}
              </div>

              {/* Add Comment */}
              {reviewStatus === 'open' && (
                <div className="flex gap-2">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add your feedback on this version..."
                    className="flex-1 min-h-20"
                  />
                  <Button
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || submitting}
                    className="self-end"
                  >
                    {submitting ? 'Adding...' : 'Comment'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-6 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {isOwner ? (
                <>👑 You can implement any revision that has team approval</>
              ) : (
                <>📝 Share your feedback to help choose the best version</>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {isOwner && reviewStatus === 'open' && (
                <Button
                  variant="outline"
                  onClick={() => setReviewStatus('closed')}
                >
                  Close Review
                </Button>
              )}
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}